import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, OnDestroy, runInInjectionContext, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveContainer } from '../../../core/containers/reactive-container';
import { FilterDateSelectValue } from '../../../core/entities/filterDateSelect/filter-date-select-value';
import { SortField, SortValue } from '../../../core/entities/sort/sort';
import { FilterStateService } from '../../../core/services/filter-state/filter-state.service';
import { SidebarService } from '../../../core/services/sidebar/sidebar.service';
import { Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { RolesService } from '../../../core/services/roles/roles.service';
import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';
import { TabView } from 'primeng/tabview';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import { FieldConfig } from 'src/app/shared-module/components/field-selector/field-selector.component';
import { LocationEntity } from 'src/app/shared-module/shared/entities/location/location';
import { LocationService } from '../../shared/services/location/location.service';
import { TaskTypeService } from '../../shared/services/taskType/taskType.service';
import { isValidTaskListFilterState, TaskListFilterState, DEFAULT_TASK_LIST_FILTER_STATE } from './task-list-filter-state.interface';
import * as fromGraphQl from './graphql';

@Component({
  selector: 'tasks-list',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TasksComponent implements Reloadable, OnDestroy {

  // Injected services
  readonly sidebarService = inject(SidebarService);
  readonly rolesService = inject(RolesService);
  private readonly locationService = inject(LocationService);
  private readonly taskTypeService = inject(TaskTypeService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly filterStateService = inject(FilterStateService);
  private readonly destroyRef = inject(DestroyRef);

  // Signal inputs for reusability
  readonly showPageTitle = input(true);
  readonly showTabs = input(true);
  
  // Filter persistence input - set a key to enable persistence
  readonly filterStorageKey = input<string>();

  // ==================== UNIFIED FILTER STATE ====================
  // Single source of truth for all filter settings (except pageNumber)
  readonly filterState = signal<TaskListFilterState>(DEFAULT_TASK_LIST_FILTER_STATE);

  // Computed accessors for template bindings
  readonly selectedFilter = computed(() => this.filterState().tabIndex);
  readonly pageSize = computed(() => this.filterState().pageSize);
  readonly typesModel = computed(() => this.filterState().typeCodes);
  readonly locationsModel = computed(() => this.filterState().locationIds);
  readonly statesModel = computed(() => this.filterState().stateCodes);
  readonly dueDateModel = computed(() => this.filterState().dueDate);
  readonly completedDateModel = computed(() => this.filterState().completedDate);
  readonly displayedFields = computed(() => this.filterState().displayedFields ?? ['address', 'order', 'customer', 'resources']);
  readonly showMetadata = computed(() => this.filterState().showMetadata ?? true);

  // Computed binaryStatus based on selected tab - only when tabs are visible
  readonly binaryStatus = computed(() => {
    // Don't apply binaryStatus filter when tabs are not shown
    if (!this.showTabs()) return undefined;
    
    const index = this.filterState().tabIndex;
    if (index === 0) return 'open';
    if (index === 1) return 'closed';
    return undefined; // "Alle" - no filter
  });

  // Sort computed from filterState
  readonly sortModel = computed<SortValue>(() => {
    const sortState = this.filterState().sort;
    const field = this.sortFields.find(f => f.key === sortState.key) ?? this.sortFields[0];
    return { field, direction: sortState.direction ?? 'asc' };
  });

  // Pagination - pageNumber is NOT persisted (always start at 0)
  readonly pageNumber = signal(0);

  // Sort field options
  readonly sortFields: SortField[] = [
    { label: 'Typ', fieldName: 'type', key: 'type' },
    { label: 'Fällig', fieldName: 'dueDateTimePeriod', key: 'due' },
    { label: 'Erledigt', fieldName: 'completedDateTime', key: 'completed' }
  ];

  // Field configuration for dynamic display/hide
  readonly availableFields: FieldConfig[] = [
    { field: 'address', label: 'Adresse' },
    { field: 'order', label: 'Auftrag' },
    { field: 'customer', label: 'Kunde' },
    { field: 'resources', label: 'Ressourcen' },
    { field: 'postalCode', label: 'Postleitzahl' },
    { field: 'city', label: 'Stadt' }
  ];

  // Global search signal (not persisted)
  readonly globalSearchText = signal<string>('');

  // State options
  readonly statesOptions = [
    { id: 'COMPLETED', name: 'Erledigt', label: 'Erledigt' },
    { id: 'ASSIGNED', name: 'Zugewiesen', label: 'Zugewiesen' },
    { id: 'NEW', name: 'Neu', label: 'Neu' },
    { id: 'UNASSIGNED', name: 'Nicht zugewiesen', label: 'Nicht zugewiesen' }
  ];

  private readonly injector = inject(Injector);

  // Global search input reference
  readonly globalSearchInput = viewChild<ElementRef<HTMLInputElement>>('globalSearch');
  readonly tabView = viewChild<TabView>('tabView');

  // Load options via toSignal
  readonly locationsOptions = toSignal(
    this.locationService.getLocations().pipe(
      map(locations => locations ? LocationEntity.createLocations(locations) : [])
    ),
    { initialValue: [] }
  );

  readonly typeOptions = toSignal(
    this.taskTypeService.getTaskTypes().pipe(
      map(taskTypes => taskTypes.map(t => ({ id: t.code, name: t.name, label: t.name })))
    ),
    { initialValue: [] }
  );

  // Refetch coordinator registration (list view - no entityId)
  private readonly unregisterRefetch = this.refetchCoordinator.register(
    this,
    Task.ENTITY_TYPE
  );

  // Track if state has been restored to prevent multiple restorations
  private _stateRestored = false;

  constructor() {
    // Restore filter state when filterStorageKey becomes available
    effect(() => {
      const key = this.filterStorageKey();
      if (key && !this._stateRestored) {
        this._stateRestored = true;
        const savedState = this.filterStateService.restore<TaskListFilterState>(
          key,
          isValidTaskListFilterState
        );
        if (savedState) {
          untracked(() => this.filterState.set(savedState));
        }
      }
    });

    // Global search input debounce with proper cleanup
    effect(() => {
      const inputEl = this.globalSearchInput();
      if (inputEl) {
        fromEvent(inputEl.nativeElement, 'input').pipe(
          map((event: Event) => (event.target as HTMLInputElement).value),
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(value => {
          this.globalSearchText.set(value);
          this.pageNumber.set(0);
        });
      }
    });

    // Update inkbar after totalElements changes (badge causes layout shift)
    effect(() => {
      this.totalElements();
      runInInjectionContext(this.injector, () => {
        afterNextRender(() => this.tabView()?.updateInkBar());
      });
    });

    // Auto-save filter state when it changes
    effect(() => {
      const key = this.filterStorageKey();
      const state = this.filterState();
      
      untracked(() => {
        if (key) {
          this.filterStateService.save(key, state);
        }
      });
    });
  }

  // ReactiveContainer replaces TasksListDataSource
  private readonly container = new ReactiveContainer<fromGraphQl.TasksListQueryResponse>(
    fromGraphQl.TASKS_LIST_QUERY,
    {
      useGlobalLoading: false,
      pollInterval: 60000,
      stopPollingOnSuccess: false
    }
  );

  // Reactive query - auto-refetches when any filter signal changes
  readonly data = this.container.fromSignals(
    [
      this.pageNumber, this.pageSize, this.sortModel,
      this.typesModel, this.locationsModel, this.statesModel,
      this.binaryStatus, this.dueDateModel, this.completedDateModel, this.globalSearchText
    ],
    ([page, size, sortValue, types, locations, states, binaryStatus, dueDate, completedDate, searchText]) => ({
      pageNumber: page,
      pageSize: size,
      sortProperty: sortValue.field.fieldName,
      sortDirection: sortValue.direction,
      types: types.length > 0 ? types : undefined,
      locationIds: locations?.length > 0 ? locations : undefined,
      states: states.length > 0 ? states : undefined,
      binaryStatus: binaryStatus,
      fromDueDateTime: dueDate?.fromDate || undefined,
      toDueDateTime: dueDate?.toDate || undefined,
      includeOverDue: dueDate?.includeOverDue ?? false,
      fromCompletedDateTime: completedDate?.fromDate || undefined,
      toCompletedDateTime: completedDate?.toDate || undefined,
      text: searchText || undefined
    })
  );

  // Derived signals from container
  readonly tasks = this.container.selectWithDefault(
    d => Task.createTasks(d.tasks?.content ?? []),
    []
  );

  readonly totalElements = this.container.selectWithDefault(
    d => d.tasks?.totalElements ?? 0,
    0
  );

  readonly loading = this.container.loading;



  // For Reloadable interface
  readonly reloadable: Reloadable = this;

  // ==================== UPDATE METHODS ====================
  // All filter changes go through filterState.update()

  onFilterChange(event: any): void {
    this.filterState.update(s => ({ ...s, tabIndex: event.index }));
    this.pageNumber.set(0);
  }

  reload(): void {
    console.log('TasksComponent: reload triggered');
    this.container.reload();
  }

  ngOnDestroy(): void {
    this.unregisterRefetch();
    // Close sidebar when leaving
    if (this.sidebarService.isOpen()) {
      this.sidebarService.close();
    }
  }

  onPageChange(page: number): void {
    this.pageNumber.set(page);
  }

  onPageSizeChange(newPageSize: number): void {
    this.filterState.update(s => ({ ...s, pageSize: newPageSize }));
    this.pageNumber.set(0);
  }

  onSortChange(sortValue: SortValue): void {
    this.filterState.update(s => ({ 
      ...s, 
      sort: { key: sortValue.field.key, direction: sortValue.direction } 
    }));
    this.pageNumber.set(0);
  }

  onTypesChange(types: string[]): void {
    this.filterState.update(s => ({ ...s, typeCodes: types }));
    this.pageNumber.set(0);
  }

  onLocationsChange(locations: string[]): void {
    this.filterState.update(s => ({ ...s, locationIds: locations }));
    this.pageNumber.set(0);
  }

  onStatesChange(states: string[]): void {
    this.filterState.update(s => ({ ...s, stateCodes: states }));
    this.pageNumber.set(0);
  }

  onDueDateChange(dueDate: FilterDateSelectValue | null): void {
    this.filterState.update(s => ({ ...s, dueDate }));
    this.pageNumber.set(0);
  }

  onCompletedDateChange(completedDate: FilterDateSelectValue | null): void {
    this.filterState.update(s => ({ ...s, completedDate }));
    this.pageNumber.set(0);
  }

  onDisplayedFieldsChange(fields: string[]): void {
    this.filterState.update(s => ({ ...s, displayedFields: fields }));
  }

  onShowMetadataChange(show: boolean): void {
    this.filterState.update(s => ({ ...s, showMetadata: show }));
  }
}
